import { createSlice } from "@reduxjs/toolkit";
import { pushDynamicTab } from "../utils/dynamicTabs";

// 🔹 Versioning for state persistence
const SLICE_VERSION = "1.1";

const getAllTabs = (state) => [...state.contracts, ...state.dynamicTabs];

// Record the tab the user is leaving so a later tab closure can return to it.
const pushTabHistory = (state, tabId) => {
  if (!tabId) return;
  state.tabHistory = state.tabHistory.filter((id) => id !== tabId);
  state.tabHistory.push(tabId);
};

// 🔹 Exportable initial state (used in store.js for version checks)
export const initialState = {
  _version: SLICE_VERSION,
  contracts: [
    { id: "contracts", name: "Contracts", isDynamic: false },
    { id: "lanes", name: "Lanes", isDynamic: false },
    { id: "routes", name: "Routes", isDynamic: false },
    { id: "rates", name: "Rates", isDynamic: false },
    // { id: "templates", name: "Templates", isDynamic: false },
    // { id: "drafts", name: "Drafts", isDynamic: false },
  ],
  dynamicTabs: [],
  activeTab: 0,
  // Stack of previously active tab ids, most-recent-last — lets removeTab send the
  // user back to whichever tab/page they were actually viewing before, not tab 0.
  tabHistory: [],
  // Bumped whenever contract data changes outside of a specific tab (e.g. bulk
  // Excel update) so open contractDetails tabs know to refetch their contract.
  lastContractsUpdatedAt: 0,
  // Which lane/route/rate sections a contractDetails tab has expanded, keyed by
  // tab id. Lives here (not component state) so it survives the tab being
  // switched away from — or the whole Contracts module being left — and back.
  expandedKeysByTab: {},
};

const contractsSlice = createSlice({
  name: "contracts",
  initialState,
  reducers: {
    addTab: (state, action) => {
      const { tabData } = action.payload;
      const dynamicId =
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2, 15)}-${state.dynamicTabs.length}`;

      const prevActiveId = getAllTabs(state)[state.activeTab]?.id;
      const added = pushDynamicTab(
        state,
        {
          id: `dyn-${dynamicId}`,
          name: tabData?.name || `New Contract ${state.dynamicTabs.length + 1}`,
          type: tabData?.type || "contract",
          searchTerm: tabData?.searchTerm,
          contractId: tabData?.contractId,
          contractData: tabData?.contractData,
          originTabName: tabData?.originTabName,
          expandedDataKey: tabData?.expandedDataKey,
          isDynamic: true,
          createdAt: Date.now(),
        },
        state.contracts.length
      );
      if (added) pushTabHistory(state, prevActiveId);

    },

    removeTab: (state, action) => {
      const { tabIndex } = action.payload;
      if (tabIndex < state.contracts.length) return;

      const tabsBefore = getAllTabs(state);
      const removedId = tabsBefore[tabIndex]?.id;
      const activeId = tabsBefore[state.activeTab]?.id;
      const wasActive = tabIndex === state.activeTab;

      const dynamicIndex = tabIndex - state.contracts.length;
      state.dynamicTabs.splice(dynamicIndex, 1);
      state.tabHistory = state.tabHistory.filter((id) => id !== removedId);
      if (removedId) delete state.expandedKeysByTab[removedId];

      const tabsAfter = getAllTabs(state);

      if (!wasActive) {
        // The tab the user was viewing wasn't the one closed — stay on it, just
        // recompute its index since removing an earlier tab shifts the array.
        const newIndex = tabsAfter.findIndex((t) => t.id === activeId);
        state.activeTab = newIndex >= 0 ? newIndex : Math.min(state.activeTab, tabsAfter.length - 1);
        return;
      }

      // The active tab itself was closed — return to the last tab/page the user
      // was actually on, walking back through history until one still exists.
      while (state.tabHistory.length) {
        const candidateId = state.tabHistory.pop();
        const idx = tabsAfter.findIndex((t) => t.id === candidateId);
        if (idx >= 0) {
          state.activeTab = idx;
          return;
        }
      }

      // No usable history — fall back to the default Contracts tab.
      state.activeTab = 0;
    },

    setActiveTab: (state, action) => {
      const { index } = action.payload;
      const maxIndex = state.contracts.length + state.dynamicTabs.length - 1;
      const nextIndex = Math.min(Math.max(index, 0), maxIndex);
      if (nextIndex !== state.activeTab) {
        pushTabHistory(state, getAllTabs(state)[state.activeTab]?.id);
        state.activeTab = nextIndex;
      }
    },

    resetTabs: (state) => {
      state.dynamicTabs = [];
      state.activeTab = 0;
      state.tabHistory = [];
    },

    setSelectedTemplate: (state, action) => {
      state.selectedTemplate = action.payload;
    },

    notifyContractsUpdated: (state) => {
      state.lastContractsUpdatedAt += 1;
    },

    setExpandedKeys: (state, action) => {
      const { tabId, keys } = action.payload;
      if (!tabId) return;
      state.expandedKeysByTab[tabId] = keys;
    },
  },
});

export const {
  addTab,
  removeTab,
  setActiveTab,
  setSelectedTemplate,
  resetTabs,
  notifyContractsUpdated,
  setExpandedKeys,
} = contractsSlice.actions;

export default contractsSlice.reducer;
